'use client';

import { appCapabilities } from './app-capabilities';
import { ShowcaseSpinner } from '../../collection-showcase/scenes/playback-controls.jsx';

// Recovered from the IMAI v5 animation source; demo documents stay illustrative.
import * as React from 'react';
import {
  ShowcaseCopyContext,
  translateShowcaseNode,
} from '../../collection-showcase/translation';
import {
  useBusinessPlayback,
  typedText,
  BusinessComposer,
  BusinessIcon,
} from './business-primitives';
import {
  kr,
  R,
  Ar,
  Dr,
  Or,
  Er,
  AppUpdate,
  SampleTracker,
  jr,
} from './app-update';
import { AppWindow } from './app-window';
import { AppRevision, OrderMetrics } from './order-tables';
import { LinePlanner } from './line-planner';
export function AppsAnimation({ running: e, onInteract: t, onDone: n }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let { t: r } = useBusinessPlayback(kr, {
      running: e,
      onDone: n,
    }),
    [i, a] = React.useState(null),
    o = i ?? 'orders',
    s = r >= R.pin ? [...Ar, 'orders'] : Ar,
    c = {
      built: r < R.build ? 0 : Math.min(1, (r - R.build) / (R.built - R.build)),
      brunOverdue: r >= R.brun,
      tsukiReceived: r >= R.received,
      pressing: r >= R.press && r < R.received,
      tab: r >= R.stock && r < R.board ? 'Stock by style' : 'Orders',
      dueDates: r >= R.applied,
    },
    l = o === 'orders',
    u = r >= R.type && r < R.send ? typedText(Dr, r, R.type, Or) : '',
    d = r >= R.send && r < R.built,
    f = (e) => {
      (t(), a(e));
    };
  return (
    <div className={'flex flex-col gap-3 p-2 md:p-4'}>
      <div
        role={'group'}
        aria-label={translateShowcaseNode(showcaseCopy, 'Apps')}
        className={
          '-mx-1 flex [scrollbar-width:none] gap-1.5 overflow-x-auto px-1 md:hidden'
        }
      >
        {translateShowcaseNode(
          showcaseCopy,
          Er.map((e) => (
            <button
              key={e.key}
              type={'button'}
              aria-pressed={e.key === o}
              onClick={() => f(e.key)}
              className={`press h-10 shrink-0 rounded-full px-3.5 text-[13px] font-medium ${e.key === o ? 'text-ink bg-[var(--state-wash)]' : 'text-ink-3 bg-[var(--tint)]'}`}
            >
              {translateShowcaseNode(showcaseCopy, e.name)}
            </button>
          ))
        )}
      </div>
      <AppWindow
        apps={Er}
        active={o}
        pinned={s}
        onOpen={f}
        titleExtra={
          l && r >= R.reviewButton && r < R.applied ? <AppRevision /> : null
        }
        overlay={
          l && r >= R.reviewOpen && r < R.applied ? (
            <AppUpdate applying={r >= R.apply} />
          ) : null
        }
        footer={
          <div className={'pointer-events-auto mx-auto max-w-[620px]'}>
            {translateShowcaseNode(
              showcaseCopy,
              d && l ? (
                <p
                  className={
                    'enter text-ink-3 mb-2 flex items-center justify-center gap-2 text-[12.5px]'
                  }
                >
                  <ShowcaseSpinner className={'size-3.5'} />
                  {translateShowcaseNode(
                    showcaseCopy,
                    'Building the app\u2026'
                  )}
                </p>
              ) : null
            )}
            <BusinessComposer
              placeholder={
                r >= R.send
                  ? 'Ask IMAI to change it\u2026'
                  : 'Describe the app you need\u2026'
              }
              text={l ? u : ''}
              typing={u.length > 0 && l}
              pressed={r >= R.send - 160 && r < R.send && l}
            />
          </div>
        }
      >
        <div className={'grid h-full'}>
          {translateShowcaseNode(
            showcaseCopy,
            Er.map((e) => (
              <div
                key={e.key}
                className={'panel min-h-0 min-w-0'}
                data-active={e.key === o}
                inert={e.key !== o}
              >
                {translateShowcaseNode(
                  showcaseCopy,
                  e.key === 'orders' ? (
                    <OrderMetrics state={c} />
                  ) : e.key === 'planner' ? (
                    <LinePlanner onInteract={t} />
                  ) : (
                    <SampleTracker onInteract={t} />
                  )
                )}
              </div>
            ))
          )}
        </div>
      </AppWindow>
      <ul className={'grid grid-cols-2 gap-x-6 gap-y-2 px-1 lg:grid-cols-4'}>
        {translateShowcaseNode(
          showcaseCopy,
          appCapabilities.capabilities.map((e) => (
            <li key={e.key} className={'flex gap-2.5'}>
              <BusinessIcon
                name={jr[e.key] ?? 'pin'}
                className={'text-ink-3 mt-0.5 size-4 shrink-0'}
              />
              <span>
                <span className={'text-ink block text-[13px] font-medium'}>
                  {translateShowcaseNode(showcaseCopy, e.label)}
                </span>
                <span
                  className={
                    'text-ink-3 block text-[12.5px] leading-[1.45] max-sm:hidden'
                  }
                >
                  {translateShowcaseNode(showcaseCopy, e.text)}
                </span>
              </span>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
export const Nr = 'ABCDEFGHIJKLMNOP';
export const Pr = (e) =>
  `${e < 0 ? '\u2212' : ''}$${Math.round(Math.abs(e)).toLocaleString('en-US')}`;
export const Fr = (e) => `${e.toFixed(1)}%`;
export const Ir = (e) => Math.round(e).toLocaleString('en-US');
export const Lr = (e) => `${e < 0 ? '\u2212' : ''}$${Math.round(Math.abs(e))}k`;
export function Rr(e, t, n = 520) {
  let r = null;
  for (let [i, a] of e.entries())
    for (let [e, o] of a.cells.entries()) {
      if (!o.f || o.at === void 0) continue;
      let a = o.at - n;
      t >= a &&
        (r === null || a >= r.start) &&
        (r = {
          r: i,
          c: e,
          formula: o.f,
          start: a,
        });
    }
  if (r === null) return null;
  let { r: i, c: a, formula: o, start: s } = r,
    c = Math.min(
      o.length,
      Math.ceil(((t - s) / Math.max(1, n - 60)) * o.length)
    );
  return {
    r: i,
    c: a,
    ref: `${'ABCDEFGHIJKLMNOP'[a] ?? 'A'}${i + 1}`,
    text: o.slice(0, c),
    typing: c < o.length,
  };
}
export function zr(e) {
  return e.node ? e.node : e.n === void 0 ? (e.v ?? '') : (e.fmt ?? Ir)(e.n);
}
export const Br = {
  w: '96px',
  hideSm: !0,
};
